import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Select,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message,
  Tabs,
  Divider,
  Input
} from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  SendOutlined,
  BankOutlined,
  WalletOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ArrowUpOutlined,
  QrcodeOutlined,
  RiseOutlined,
  FallOutlined,
  FilterOutlined,
  ExportOutlined,
  SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// Create axios instance with JWT authentication for merchant
const merchantApi = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Request interceptor to add JWT token
merchantApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('merchant_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
merchantApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('merchant_access_token');
      localStorage.removeItem('merchant_refresh_token');
      localStorage.removeItem('merchant_user');
      window.location.href = '/merchant/login';
    }
    return Promise.reject(error);
  }
);

const MerchantCashInOut = () => {
  const [loading, setLoading] = useState(false);
  const [cashInData, setCashInData] = useState([]);
  const [cashOutData, setCashOutData] = useState([]);
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState('cash-in');
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: null,
    date_range: null,
  });

  // Get merchant info from localStorage
  const [merchantData, setMerchantData] = useState(null);
  const [merchantId, setMerchantId] = useState(null);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('merchant_user');
      console.log('Stored merchant data:', storedData);
      if (storedData) {
        const parsed = JSON.parse(storedData);
        console.log('Parsed merchant data:', parsed);
        setMerchantData(parsed);

        // Use user.id for settlements route (not member_id)
        const merchantId = parsed.user_id || parsed.id;
        setMerchantId(merchantId);
        console.log('Setting merchant ID:', merchantId, 'from fields:', {
          user_id: parsed.user_id,
          id: parsed.id,
          member_id: parsed.member_id
        });
      } else {
        // For development/testing - use test merchant ID
        console.log('No merchant data found, using fallback ID 2');
        setMerchantId(2);
      }
    } catch (error) {
      console.error('Error parsing merchant data:', error);
      setMerchantId(2);
    }
  }, []);

  useEffect(() => {
    if (merchantId) {
      if (activeTab === 'cash-in') {
        fetchCashInData();
      } else if (activeTab === 'cash-out') {
        fetchCashOutData();
      }
      fetchSummary();
    }
  }, [filters, activeTab, merchantId]);

  const fetchCashInData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      params.append('merchant_id', merchantId);
      params.append('limit', '1000');
      console.log('Fetching cash in data for merchant ID:', merchantId);

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const url = `/merchant/finance/cash-in?${params.toString()}`;
      const response = await merchantApi.get(url);
      console.log('Cash in response:', response.data);
      setCashInData(response.data.data || []);
    } catch (error) {
      console.error('Fetch cash in error:', error);
      setCashInData([]);
      message.warning(`No cash in data available for merchant ID ${merchantId}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashOutData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      params.append('merchant_id', merchantId);
      params.append('limit', '1000');

      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const response = await merchantApi.get(`/merchant/finance/cash-out?${params.toString()}`);
      setCashOutData(response.data.data || []);
    } catch (error) {
      console.error('Fetch cash out error:', error);
      setCashOutData([]);
      message.warning(`No cash out data available for merchant ID ${merchantId}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();

      params.append('merchant_id', merchantId);
      console.log('Fetching summary for merchant ID:', merchantId);

      if (filters.date_range && filters.date_range[0]) {
        params.append('date_from', filters.date_range[0].format('YYYY-MM-DD'));
        params.append('date_to', filters.date_range[1].format('YYYY-MM-DD'));
      }

      const url = `/merchant/finance/cash-flow-summary?${params.toString()}`;
      const response = await merchantApi.get(url);
      console.log('Summary response:', response.data);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Fetch summary error:', error);
      // Set empty summary data on error so the UI doesn't break
      setSummary({
        total_cash_in_today: 0,
        total_cash_out_today: 0,
        net_cash_flow_today: 0,
        total_transactions_today: 0,
        cash_in_transactions: 0,
        cash_out_transactions: 0
      });
      message.warning('Failed to load summary data for this merchant');
    }
  };

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';

    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(numericAmount);
  };

  const getTransactionTypeTag = (type, flow) => {
    const configs = {
      // Cash In types
      'qris_payment': { color: 'green', text: 'QRIS Payment' },
      'va_payment': { color: 'blue', text: 'VA Payment' },
      'ewallet_payment': { color: 'purple', text: 'E-Wallet Payment' },
      'deposit': { color: 'cyan', text: 'Deposit' },

      // Cash Out types
      'withdrawal': { color: 'red', text: 'Withdrawal' },
      'fee_deduction': { color: 'orange', text: 'Fee Deduction' },
      'topup': { color: 'volcano', text: 'Top Up' },
      'bifast_transfer': { color: 'geekblue', text: 'BIFAST Transfer' },
      'bill_payment': { color: 'magenta', text: 'Bill Payment' }
    };

    const config = configs[type] || { color: 'default', text: type };

    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getStatusTag = (status) => {
    const statusConfigs = {
      'paid': { color: 'green', text: 'Paid' },
      'success': { color: 'green', text: 'Success' },
      'pending': { color: 'orange', text: 'Pending' },
      'failed': { color: 'red', text: 'Failed' },
      'expired': { color: 'volcano', text: 'Expired' },
      'cancelled': { color: 'default', text: 'Cancelled' }
    };

    const config = statusConfigs[status] || { color: 'default', text: status };

    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getCommonColumns = (isCashIn) => {
    return [
      {
        title: 'Transaction ID',
        dataIndex: 'transaction_id',
        key: 'transaction_id',
        width: 150,
        render: (text) => (
          <Text code style={{ fontSize: '12px' }}>{text}</Text>
        )
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
        align: 'right',
        render: (amount) => (
          <Text strong style={{ color: isCashIn ? '#52c41a' : '#ff4d4f' }}>
            {formatCurrency(amount)}
          </Text>
        )
      },
      ...(!isCashIn ? [{
        title: 'Tujuan',
        key: 'destination',
        width: 170,
        render: (_, record) => {
          if (!record.destination_number && !record.destination_name) return <Text type="secondary">-</Text>;
          return (
            <div>
              <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>{record.destination_number || '-'}</div>
              <div style={{ fontSize: 11, color: '#555' }}>{record.destination_name || ''}</div>
              {record.destination_bank && <div style={{ fontSize: 11, color: '#999' }}>{record.destination_bank}</div>}
            </div>
          );
        }
      }] : []),
      {
        title: 'Payment Method',
        dataIndex: 'payment_method',
        key: 'payment_method',
        width: 120,
        render: (method) => <Tag color="blue">{method}</Tag>
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status) => getStatusTag(status)
      },
      {
        title: 'Date',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 120,
        render: (date) => dayjs(date).format('DD/MM/YY HH:mm')
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, record) => (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          />
        )
      }
    ];
  };

  const exportData = () => {
    const isCashIn = activeTab === 'cash-in';
    const data = isCashIn ? cashInData : cashOutData;

    if (data.length === 0) {
      message.warning('Tidak ada data untuk di-export');
      return;
    }

    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const headers = isCashIn
      ? ['Transaction ID', 'Tipe', 'Amount', 'Status', 'Tanggal']
      : ['Transaction ID', 'Tipe', 'Amount', 'Nomor Tujuan', 'Nama Tujuan', 'Bank/Dompet', 'Status', 'Tanggal'];

    const rows = data.map(row => {
      const base = [row.transaction_id, row.transaction_type, row.amount];
      const tujuan = isCashIn ? [] : [
        row.destination_number || '',
        row.destination_name || '',
        row.destination_bank || '',
      ];
      return [...base, ...tujuan, row.status, dayjs(row.created_at).format('YYYY-MM-DD HH:mm')];
    });

    const csvContent = [
      headers.map(esc).join(','),
      ...rows.map(row => row.map(esc).join(','))
    ].join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `merchant-${activeTab}-${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    message.success('Data berhasil di-export');
  };

  if (!merchantId) {
    return (
      <div style={{ padding: '24px' }}>
        <Card loading={true}>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <h3>Loading Merchant Data...</h3>
            <p>Sedang memuat data merchant...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 8 }}>
        Cash Flow Report
      </Title>
      <Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>
        Laporan arus kas masuk dan keluar untuk merchant Anda{merchantData ? ` (${merchantData.full_name || merchantData.merchant_name})` : ''}
      </Text>

      {merchantData && (
        <div style={{ marginBottom: 16, padding: '12px', background: '#f6f7fa', borderRadius: '6px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <strong>Merchant Info:</strong> {merchantData.email} | ID: {merchantId} |
            Showing transactions filtered to your merchant account only
          </Text>
        </div>
      )}

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Cash In Today"
              value={summary.total_cash_in_today || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => `Rp ${Number(value).toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Cash Out Today"
              value={summary.total_cash_out_today || 0}
              prefix={<FallOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              formatter={(value) => `Rp ${Number(value).toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Net Cash Flow"
              value={summary.net_cash_flow_today || 0}
              prefix={summary.net_cash_flow_today >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{
                color: summary.net_cash_flow_today >= 0 ? '#52c41a' : '#ff4d4f'
              }}
              formatter={(value) => `Rp ${Number(value).toLocaleString('id-ID')}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={summary.total_transactions_today || 0}
              prefix={<SendOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={
            <Space>
              <Button
                icon={<ExportOutlined />}
                onClick={exportData}
                disabled={
                  (activeTab === 'cash-in' ? cashInData.length : cashOutData.length) === 0
                }
              >
                Export CSV
              </Button>
            </Space>
          }
        >
          <TabPane tab="Cash In" key="cash-in">
            {/* Filters for Cash In */}
            <div style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Search..."
                    prefix={<SearchOutlined />}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.status}
                    onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  >
                    <Option value="paid">Paid</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="failed">Failed</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={filters.date_range}
                    onChange={(dates) => setFilters(prev => ({ ...prev, date_range: dates }))}
                  />
                </Col>
              </Row>
            </div>

            <Table
              columns={getCommonColumns(true)}
              dataSource={cashInData}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} transactions`,
                onShowSizeChange: (_, size) => setPageSize(size)
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>

          <TabPane tab="Cash Out" key="cash-out">
            {/* Filters for Cash Out */}
            <div style={{ marginBottom: 16 }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input
                    placeholder="Search..."
                    prefix={<SearchOutlined />}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.status}
                    onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  >
                    <Option value="success">Success</Option>
                    <Option value="pending">Pending</Option>
                    <Option value="failed">Failed</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={filters.date_range}
                    onChange={(dates) => setFilters(prev => ({ ...prev, date_range: dates }))}
                  />
                </Col>
              </Row>
            </div>

            <Table
              columns={getCommonColumns(false)}
              dataSource={cashOutData}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} transactions`,
                onShowSizeChange: (_, size) => setPageSize(size)
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedRecord && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Transaction ID">
              <Text code>{selectedRecord.transaction_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ color: activeTab === 'cash-in' ? '#52c41a' : '#ff4d4f' }}>
                {formatCurrency(selectedRecord.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method">
              <Tag color="blue">{selectedRecord.payment_method}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {getStatusTag(selectedRecord.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Reference ID">
              <Text code>{selectedRecord.reference_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedRecord.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(selectedRecord.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {dayjs(selectedRecord.updated_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantCashInOut;